import {LocalizedString} from "../i18n/i18n-types";

export interface DatabaseCode {
  code: string;
  displayValue: LocalizedString;
}

export function mapDatabaseCodes<D extends DatabaseCode>(
  codes: string[] | undefined,
  mapper: (code: string) => D
): D[] | undefined {
  if (!codes) {
    return undefined;
  }

  return codes.map((c) => mapper(c));
}

export function toDatabaseCode<D extends DatabaseCode>(
  code: string,
  types: D[] = [],
  defaultValue: D | undefined = undefined
): D {
  for (const type of types) {
    if (type.code === code) {
      return type;
    }
  }

  return defaultValue ?? {
    code: code,
    displayValue: {
      id: code,
      defaultMessage: code
    }
  } as D;
}

export function getDatabaseCode<D extends DatabaseCode>(code: string | undefined, types: D[]): D | undefined {
  if (code) {
    for (const type of types) {
      if (type.code === code) {
        return type;
      }
    }
  }

  return undefined
}

export function toDatabaseCodeSafely<D extends DatabaseCode>(
  code: string | null | undefined,
  types: D[] = [],
  defaultValue: D | undefined = undefined
): D | undefined {
  if (code) {
    return toDatabaseCode(code, types, defaultValue)
  }

  return undefined
}

export const isDatabaseCode = (value: string | DatabaseCode | number): value is DatabaseCode => {
  return typeof value !== 'string' && typeof value !== 'number' && 'code' in value
};

export const getDatabaseCodeSafely = (value: DatabaseCode | string | undefined) : string | undefined => {
  if (value) {
    if (isDatabaseCode(value)) {
      return value.code
    } else {
      return value
    }
  }

  return undefined
}
